import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LocationChangeModal({ isOpen, onClose, onUpdate, currentCountry, detectedCountry }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const countries = {
    'DE': { name: t('country.germany', 'Deutschland'), flag: '🇩🇪' },
    'AT': { name: t('country.austria', 'Österreich'), flag: '🇦🇹' },
    'CH': { name: t('country.switzerland', 'Schweiz'), flag: '🇨🇭' },
    'OTHER': { name: t('country.other', 'Anderes Land'), flag: '🌍' }
  };

  const handleUpdateCountry = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/country/update`,
        null,
        {
          params: { country_code: detectedCountry },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      onUpdate(detectedCountry);
    } catch (error) {
      console.error('Failed to update country:', error);
      alert(t('locationChange.errorUpdating', 'Fehler beim Aktualisieren des Landes'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeepCurrent = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full my-8 p-4 sm:p-6 animate-fade-in">
        {/* Icon */}
        <div className="flex items-center justify-center w-14 h-14 mx-auto bg-orange-100 rounded-full mb-4">
          <svg className="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
          {t('locationChange.title', 'Standortänderung erkannt')}
        </h3>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6">
          {t('locationChange.message', 'Wir haben festgestellt, dass Sie sich möglicherweise in einem anderen Land befinden.')}
        </p>

        {/* Country Comparison */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('locationChange.current', 'Aktuelle Steuerpflicht')}:
            </span>
            <span className="font-semibold text-gray-900">
              {countries[currentCountry]?.flag} {countries[currentCountry]?.name}
            </span>
          </div>
          <div className="border-t border-gray-200"></div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('locationChange.detected', 'Erkannter Standort')}:
            </span>
            <span className="font-semibold text-orange-600">
              {countries[detectedCountry]?.flag} {countries[detectedCountry]?.name}
            </span>
          </div>
        </div>

        {/* Question */}
        <p className="text-center text-gray-700 font-medium mb-6">
          {t('locationChange.question', 'Hat sich Ihr Land der Steuerpflicht geändert?')}
        </p>

        {/* Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleUpdateCountry}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('locationChange.updating', 'Aktualisiere...')}
              </span>
            ) : (
              <>
                {t('locationChange.yesUpdate', 'Ja, zu')} {countries[detectedCountry]?.name} {t('locationChange.change', 'wechseln')}
              </>
            )}
          </button>
          <button
            onClick={handleKeepCurrent}
            disabled={loading}
            className="w-full bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {t('locationChange.keepCurrent', 'Nein, bei')} {countries[currentCountry]?.name} {t('locationChange.stay', 'bleiben')}
          </button>
        </div>

        {/* Info */}
        <p className="text-xs text-gray-500 text-center mt-4">
          {t('locationChange.info', 'Sie können Ihre Steuerpflicht jederzeit in den Einstellungen ändern.')}
        </p>
      </div>
    </div>
  );
}

export default LocationChangeModal;
